// The picture a shared link to a post or a chapter shows beside its title — in
// a text message, Slack, or a social card. Only the editor shows it; the page
// itself does not, since it is the link's picture, not the page's.
//
// `prepareImage` redraws the chosen file before it is sent; this is only the
// choosing, and what to say when reading the file fails.
import { imageUrl } from "@bibleunleashed/shared";
import { useRef, useState } from "react";
import { useUploadImage } from "@/lib/posts";
import { prepareImage } from "./prepareImage";

export function PreviewImageField({ image, onChange }: { image: string | null; onChange: (image: string | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadImage();
  // Reading the file can fail before anything is sent (an iPhone's HEIC, in most browsers).
  const [error, setError] = useState<string | null>(null);

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    let blob: Blob;
    try {
      blob = await prepareImage(file);
    } catch {
      return setError("That file couldn’t be read as an image. Try a PNG, JPEG or WebP.");
    }
    try {
      onChange((await upload.mutateAsync(blob)).id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The upload failed");
    }
  };

  const btn = "rounded-md border border-rule px-2.5 py-1 font-sans text-[12px] text-ink-2 hover:text-ink disabled:opacity-50";
  return (
    <div>
      <span className="mb-0.5 block font-sans text-[11px] tracking-[0.06em] text-ink-3 uppercase">
        Link preview image
      </span>
      <div className="flex flex-wrap items-center gap-3">
        {image && (
          <img
            src={imageUrl(image)}
            alt="How a shared link to this page will look"
            className="h-[84px] w-[160px] rounded border border-rule object-cover"
          />
        )}
        <div className="flex flex-col gap-1.5">
          <div className="flex gap-2">
            <button type="button" className={btn} disabled={upload.isPending} onClick={() => input.current?.click()}>
              {upload.isPending ? "Uploading…" : image ? "Replace" : "Choose image"}
            </button>
            {image && (
              <button type="button" className={btn} disabled={upload.isPending} onClick={() => onChange(null)}>
                Remove
              </button>
            )}
          </div>
          <p className="font-sans text-[11.5px] text-ink-3">
            {error
              ? error
              : image
                ? "Shown when a link to this page is shared."
                : "None: a shared link shows the site’s own. Landscape, about 1200 × 630, suits most apps."}
          </p>
        </div>
      </div>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => {
          void choose(e.target.files?.[0]);
          e.target.value = ""; // so choosing the same file again still fires
        }}
      />
    </div>
  );
}
